import { memo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/app/store";
import { AlertTriangle } from "lucide-react";
import { CutIcon } from "@assets/Icons";
import { Button, Typography } from "@cointervue/design-system";
import { cn } from "@cointervue/shared";
import { dismissQuotaBanner } from "@/store/authSlice";

const QUOTA_WARNING_THRESHOLD = 3;

const BANNER_THEMES = {
  exhausted: {
    container: "bg-brand-score-light-red-light border-brand-score-light-red/30",
    text: "text-brand-error-red",
    icon: "text-brand-error-red",
    buttonHover: "hover:bg-brand-score-light-red/20 text-brand-score-light-red-text",
  },
  warning: {
    container: "bg-brand-score-dark-yellow-light border-brand-score-dark-yellow/30",
    text: "text-brand-score-dark-yellow-text",
    icon: "text-brand-score-dark-yellow-text",
    buttonHover: "hover:bg-brand-score-dark-yellow/20 text-brand-score-dark-yellow-text",
  },
} as const;

export const QuotaWarningBanner = memo(function QuotaWarningBanner() {
  const dispatch = useAppDispatch();
  const interviews = useAppSelector((state) => state.auth.interviews);
  const totalAllowedInterviews = useAppSelector(
    (state) => state.auth.totalAllowedInterviews
  );
  const isDismissed = useAppSelector(
    (state) => state.auth.isQuotaBannerDismissed
  );

  const handleDismiss = useCallback(() => {
    dispatch(dismissQuotaBanner());
  }, [dispatch]);

  if (
    isDismissed ||
    interviews === undefined ||
    totalAllowedInterviews === undefined ||
    isNaN(interviews) ||
    isNaN(totalAllowedInterviews)
  ) {
    return null;
  }

  const remaining = totalAllowedInterviews - interviews;

  if (remaining > QUOTA_WARNING_THRESHOLD) {
    return null;
  }

  const isExhausted = remaining <= 0;
  const theme = isExhausted ? BANNER_THEMES.exhausted : BANNER_THEMES.warning;

  return (
    <aside
      aria-label="Quota Alert"
      className={cn(
        "border-b px-8 py-2 relative flex items-center justify-center z-40 shrink-0 select-none shadow-2xs",
        theme.container
      )}
    >
      <div className="flex items-center justify-center gap-2 text-center min-w-0">
        <AlertTriangle className={cn("w-4 h-4 shrink-0", theme.icon)} />
        <Typography
          variant="bodySmall"
          className={cn(
            "truncate sm:whitespace-normal text-xs sm:text-sm font-medium",
            theme.text
          )}
        >
          {remaining > 0 ? (
            <span>
              You have only{" "}
              <span className="font-bold text-brand-black">
                {remaining} {remaining === 1 ? "interview" : "interviews"}
              </span>{" "}
              remaining ({interviews}/{totalAllowedInterviews} used). Please
              contact sales.
            </span>
          ) : (
            <span>
              You have reached your limit of{" "}
              <span className="font-bold text-brand-error-red">
                {totalAllowedInterviews}
              </span>{" "}
              allowed interviews ({interviews}/{totalAllowedInterviews} used).
              Please contact sales.
            </span>
          )}
        </Typography>
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={handleDismiss}
        className={cn(
          "right-3 w-auto h-auto p-1 transition-colors cursor-pointer flex items-center justify-center border-0 shadow-none rounded-full",
          theme.buttonHover
        )}
        title="Dismiss banner"
        aria-label="Dismiss banner"
      >
        <img
          src={CutIcon}
          alt="Dismiss"
          className="w-3.5 h-3.5 opacity-80 hover:opacity-100"
        />
      </Button>
    </aside>
  );
});

export default QuotaWarningBanner;
